import { useState } from 'react';

import { Button, FormRow } from '@ui';
import { Form } from 'components/Form/base/Form';
import { FormInput } from 'components/Form/next';
import { SubmitInput } from 'components/Form/SubmitInput';
import Loader from 'components/Loader';
import { ExemptionsAssistent, exemptionsRules } from 'components/SpaceBooking/ExemptionsAssistent';
import { Space, SpaceBlocking, SpaceBlockingGroup } from 'components/SpaceBooking/types';
import Flex from 'components/styledComponents/Flex';
import { timeDivisions } from 'components/TrainingPlan/TrainingPlanHelpers';
import checkAccess from 'hoc/checkAccess';
import Joi from 'joi';
import { Moment } from 'moment';
import useSWR from 'swr';
import { useApi } from '../../../../hooks/useApi';
import { HypotheticalPriceComponent } from './HypotheticalPriceComponent';

interface Props {
    clubSlug: string;
    startTime?: number;
    startDate?: Moment;
    space?: Space;
    blocking?: SpaceBlocking;
    callback?: (blocking?: SpaceBlocking) => void;
    fetch?: true;
}

export const SpaceBlockingForm = checkAccess(
    ({ clubSlug, space, blocking: _blocking, callback, startDate, startTime, fetch }: Props) => {
        const api = useApi();
        const {
            data: blockingData,
            isLoading: blockingIsLoading,
            mutate
        } = useSWR(fetch && _blocking.id ? `space/blocking/${_blocking.id}` : null);
        const blocking = fetch ? blockingData?.data || _blocking : _blocking;

        const { data: spaces, isLoading: spacesIsLoading } = useSWR<{
            data: Space[];
        }>(`space/${clubSlug}`);
        const { data: groups, isLoading: groupsIsLoading } = useSWR<{ data: SpaceBlockingGroup[] }>(
            `space/blocking/group/${clubSlug}`
        );

        const dataIsLoading = spacesIsLoading || groupsIsLoading || blockingIsLoading;
        const [isLoading, setIsLoading] = useState(false);

        const onSubmit = (data) => {
            setIsLoading(true);
            return api({
                url: `space/blocking/${blocking?.id || clubSlug}`,
                method: blocking ? 'PUT' : 'POST',
                data: {
                    ...data,
                    // start: moment(data.start).subtract(1, "day"),
                    spaces: data.spaces?.map((space) => space.id)
                },
                onBefore: () => setIsLoading(false),
                onSuccess: ({ data }) => {
                    mutate({ data });
                    callback?.(data);
                }
            });
        };

        const handleDelete = () => {
            api({
                url: `space/blocking/${blocking.id}`,
                method: 'DELETE',
                onSuccess: () => callback?.()
            }).catch(() => {});
        };

        const schema = Joi.object({
            reason: Joi.string().required().label('Begründung'),
            description: Joi.string().allow('', null).label('Beschreibung'),
            remarks: Joi.string().allow('', null).label('Anmerkungen'),
            start: Joi.date().required().label('Startzeit'),
            start_time: Joi.number().required().label('Startzeit'),
            end_time: Joi.number()
                .required()
                .min(Joi.ref('start_time'))
                .message('Deine Startzeit muss vor deiner Endzeit sein')
                .label('Endzeit'),
            repeat_period: Joi.string().valid('', null, 'daily', 'weekly', 'monthly', 'yearly').label('Wiederholen'),
            repeat_until: Joi.date().allow('', null).label('Wiederholen bis'),
            space_blocking_group_id: Joi.number().min(0).allow('', null).label('Gruppe'),
            spaces: Joi.array()
                .min(1)
                .items(Joi.object({ id: Joi.number().min(0).label('Platz ID') }).unknown())
                .label('Plätze'),
            exemptions: exemptionsRules
        }).unknown();

        return (
            <Loader isLoading={dataIsLoading}>
                {blocking?.id && (
                    <Button className="tw-mb-8 tw-w-full !tw-bg-error tw-py-5" onPress={handleDelete}>
                        Blockierung löschen
                    </Button>
                )}
                <Form
                    onSubmit={onSubmit}
                    validationSchema={schema}
                    setDefaultValuesOnRerender={false}
                    defaultValuesDeps={[spaces]}
                    defaultValues={{
                        start: startDate?.toDate() || new Date(),
                        start_time: startTime || 28,
                        end_time: startTime + 4 || 32,
                        space_blocking_group_id: blocking?.space_blocking_group?.id,
                        ...blocking,
                        exemptions: blocking?.exemptions || [],
                        spaces: space
                            ? [{ id: space.id }]
                            : spaces
                              ? blocking?.spaces?.map((space_id) => spaces?.data.find((space) => space.id === space_id))
                              : []
                    }}
                >
                    {!space && (
                        <FormInput.AutoComplete
                            name="spaces"
                            label="Plätze"
                            data={spaces?.data || []}
                            dataKey={(space) => `${space.name} - ${space.space_group?.name}`}
                            multiple
                        />
                    )}
                    <FormInput name="reason" label="Begründung" />
                    <FormInput.Textarea
                        name="description"
                        label="Beschreibung (wird als Tooltip über der Blockierung angezeigt)"
                    />
                    <FormInput.Textarea name="remarks" label="Anmerkungen (nur für Admins sichtbar)" />
                    <FormInput.DatePicker name="start" label="Starttag" utcMode />
                    <FormRow>
                        <FormInput.Select
                            name="start_time"
                            label="Startzeit"
                            options={timeDivisions.times.map((time, index) => ({ key: index, label: time }))}
                        />
                        <FormInput.Select
                            name="end_time"
                            label="Endzeit"
                            options={timeDivisions.times.map((time, index) => ({ key: index, label: time }))}
                        />
                    </FormRow>
                    <Flex align="center" gap={0.5} className="!tw-mb-4">
                        <FormInput.Select
                            className="mt-0"
                            name="repeat_period"
                            label="Wiederholen"
                            options={[
                                { label: 'Nicht Wiederholen', key: '' },
                                { label: 'täglich', key: 'daily' },
                                { label: 'wöchentlich', key: 'weekly' },
                                { label: 'monatlich', key: 'monthly' },
                                { label: 'jährlich', key: 'yearly' }
                            ]}
                        />

                        <FormInput.DatePicker name="repeat_until" label="Wiederholen bis" utcMode />
                    </Flex>
                    <ExemptionsAssistent name="exemptions" />
                    <FormInput.Select
                        name="space_blocking_group_id"
                        label="Gruppe"
                        options={groups?.data?.map((g) => ({ key: g.id, label: g.name }))}
                    />

                    {blocking?.id && (
                        <>
                            <div className="my-2 tw-h-[1px] tw-w-full tw-bg-accents-4" />
                            <HypotheticalPriceComponent blockingId={blocking.id} />
                            <div className="my-4" />
                        </>
                    )}

                    <SubmitInput className="tw-w-full tw-rounded-lg tw-py-2" isLoading={isLoading}>
                        Speichern
                    </SubmitInput>
                </Form>
            </Loader>
        );
    }
)(['space_booking.blocking_group.view']);
